/**
 * Project Detail Page - Simplified Version
 * Comprehensive view of a single project with consolidated components
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { useSocket } from '../context/SocketContext';
import StatusBadge from '../components/common/StatusBadge';
import WorkflowAssignmentModal from '../components/projects/WorkflowAssignmentModal';
import SalesAgentAssignmentModal from '../components/projects/SalesAgentAssignmentModal';
import FileGallery from '../components/files/FileGallery';
import QuickActionButtons from '../components/projects/QuickActionButtons';
import ProjectProgressPanel from '../components/projects/ProjectProgressPanel';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatProjectNumber, formatUserName, toTitleCase } from '../utils/formatters';
import { getProjectSid, getProjectSidId, hasProjectSid } from '../utils/projectHelpers';
import { hasDirectorAccess } from '../utils/rolePermissions';

// New consolidated components
import ProjectOverviewCard from '../components/projects/ProjectOverviewCard';
import TeamCard from '../components/projects/TeamCard';
import PricingCard from '../components/projects/PricingCard';
import TimelineDrawer, { TimelineDrawerTrigger } from '../components/projects/TimelineDrawer';
import PaymentMilestonesSection from '../components/projects/PaymentMilestonesSection';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProject, fetchProjectById, loading, error } = useProjects();
  const { socket } = useSocket();

  // Modal states
  const [showAssignSidModal, setShowAssignSidModal] = useState(false);
  const [availableSids, setAvailableSids] = useState([]);
  const [selectedSidId, setSelectedSidId] = useState('');
  const [assigningSid, setAssigningSid] = useState(false);

  // Timeline state
  const [showTimeline, setShowTimeline] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Team management state
  const [showTeamAssignModal, setShowTeamAssignModal] = useState(false);
  const [teamAssignRoleType, setTeamAssignRoleType] = useState(null);
  const [removingTeamMember, setRemovingTeamMember] = useState(null);

  // File gallery refresh trigger (increment to force refresh)
  const [fileGalleryRefresh, setFileGalleryRefresh] = useState(0);

  // Sales agent management state
  const [showSalesAgentModal, setShowSalesAgentModal] = useState(false);
  const [removingSalesAgent, setRemovingSalesAgent] = useState(null);
  const [settingPrimaryAgent, setSettingPrimaryAgent] = useState(null);

  const project = selectedProject;
  const isSiteDraughter = user?.role === 'draughter_site';
  const is2DDraughter = user?.role === 'draughter_2d';
  const isRestrictedViewer = isSiteDraughter || is2DDraughter;

  // Lock team management when project is Won or Lost
  const isProjectClosed = project?.workflowStatus === 'project_closed_won' || project?.workflowStatus === 'project_closed_lost';
  const isTeamLocked = isRestrictedViewer || isProjectClosed;

  // Group ASIDs under their parent SID for the assign-SID dropdown
  const groupedSids = useMemo(() => {
    const byName = (a, b) =>
      (a.nickname || a.fullName || '').toLowerCase()
        .localeCompare((b.nickname || b.fullName || '').toLowerCase());
    const sids = availableSids.filter(u => u.role === 'sid');
    const asids = availableSids.filter(u => u.role === 'asid');
    const sidIds = new Set(sids.map(s => s.id));

    const groups = [...sids].sort(byName).map(sid => ({
      sid,
      asids: asids.filter(a => a.sidId === sid.id).sort(byName)
    }));
    const orphanAsids = asids
      .filter(a => !a.sidId || !sidIds.has(a.sidId))
      .sort(byName);

    return { groups, orphanAsids };
  }, [availableSids]);

  // Fetch project on mount
  useEffect(() => {
    if (id) {
      fetchProjectById(id);
      fetchProjectHistory();
    }
  }, [id, fetchProjectById]);

  // Fetch available SIDs on mount (for managers, directors, and leaders)
  useEffect(() => {
    if (user?.role === 'manager' || hasDirectorAccess(user?.role) || user?.role === 'leader') {
      fetchAvailableSids();
    }
  }, [user]);

  // Listen for team and status updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    const handleProjectUpdate = (data) => {
      if (data.projectId === id) {
        fetchProjectById(id);
      }
    };

    socket.on('project:team_updated', handleProjectUpdate);
    socket.on('project:status_updated', handleProjectUpdate);

    return () => {
      socket.off('project:team_updated', handleProjectUpdate);
      socket.off('project:status_updated', handleProjectUpdate);
    };
  }, [socket, id, fetchProjectById]);

  async function fetchProjectHistory() {
    if (!id) return;
    setLoadingHistory(true);
    try {
      const response = await api.get(`/design-projects/${id}/history`);
      setProjectHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching project history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function fetchAvailableSids() {
    try {
      const [sidsResponse, asidsResponse] = await Promise.all([
        api.get('/users?role=sid'),
        api.get('/users?role=asid')
      ]);

      const allSids = [
        ...(sidsResponse.data.users || []),
        ...(asidsResponse.data.users || [])
      ];

      const activeSids = allSids
        .filter(user => !user.isResigned)
        .sort((a, b) => {
          if (a.role === 'sid' && b.role === 'asid') return -1;
          if (a.role === 'asid' && b.role === 'sid') return 1;
          const aName = (a.nickname || a.fullName || '').toLowerCase();
          const bName = (b.nickname || b.fullName || '').toLowerCase();
          return aName.localeCompare(bName);
        });

      setAvailableSids(activeSids);
    } catch (error) {
      console.error('Fetch SIDs error:', error);
    }
  }

  function openAssignSidModal() {
    setSelectedSidId(getProjectSidId(project) || '');
    setShowAssignSidModal(true);
  }

  async function handleAssignSid() {
    if (!selectedSidId) {
      toast.error('Please select a SID');
      return;
    }

    try {
      setAssigningSid(true);
      await api.patch(`/design-projects/${id}/assign-sid`, { sidId: selectedSidId });
      toast.success('SID assigned successfully');
      setShowAssignSidModal(false);
      setSelectedSidId('');
      fetchProjectById(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to assign SID');
    } finally {
      setAssigningSid(false);
    }
  }

  function openTeamAssignModal(roleType) {
    setTeamAssignRoleType(roleType);
    setShowTeamAssignModal(true);
  }

  async function handleRemoveTeamMember(memberId) {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      setRemovingTeamMember(memberId);
      await api.delete(`/design-projects/${id}/team/${memberId}`);
      toast.success('Team member removed');
      fetchProjectById(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove team member');
    } finally {
      setRemovingTeamMember(null);
    }
  }

  async function handleRemoveSalesAgent(agentAssignmentId) {
    if (!window.confirm('Are you sure you want to remove this sales agent?')) return;

    try {
      setRemovingSalesAgent(agentAssignmentId);
      await api.delete(`/design-projects/${id}/sales-agents/${agentAssignmentId}`);
      toast.success('Sales agent removed');
      fetchProjectById(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove sales agent');
    } finally {
      setRemovingSalesAgent(null);
    }
  }

  async function handleSetPrimaryAgent(agentAssignmentId) {
    try {
      setSettingPrimaryAgent(agentAssignmentId);
      await api.patch(`/design-projects/${id}/sales-agents/${agentAssignmentId}/primary`);
      toast.success('Primary agent updated');
      fetchProjectById(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to set primary agent');
    } finally {
      setSettingPrimaryAgent(null);
    }
  }

  // Loading state
  if (loading && !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Project</h3>
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Project not found</h3>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 sm:pb-0">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Back button - touch friendly */}
          <button
            onClick={() => navigate(-1)}
            className="mb-3 sm:mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 min-h-[44px] -ml-2 px-2 touch-manipulation"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {/* Project header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
              {formatProjectNumber(project.projectNumber)}
            </h1>
            <p className="mt-1 sm:mt-2 text-base sm:text-lg text-gray-600 truncate">{toTitleCase(project.salesForm?.clientName) || 'N/A'}</p>
            <div className="mt-2 sm:mt-3">
              <StatusBadge status={project.workflowStatus} size="md" />
            </div>

            {/* Quick Actions - Desktop toolbar below status (hidden on mobile, FAB used instead) */}
            {!isRestrictedViewer && (
              <div className="hidden sm:block mt-4">
                <QuickActionButtons
                  project={project}
                  userRole={user?.role}
                  onActionComplete={(actionType) => {
                    fetchProjectById(id);
                    fetchProjectHistory();
                    // Refresh file gallery when files are uploaded
                    if (actionType === 'upload_files') {
                      setFileGalleryRefresh(prev => prev + 1);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Right Column - Progress & Files (FIRST on mobile for priority access) */}
          <div className="order-1 lg:order-2 space-y-4 sm:space-y-6">
            {/* Project Progress Panel - hidden for restricted viewers */}
            {!isRestrictedViewer && (
              <ProjectProgressPanel project={project} userRole={user?.role} projectHistory={projectHistory} />
            )}

            {/* Files Gallery */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <FileGallery projectId={id} userRole={user?.role} workflowStatus={project?.workflowStatus} refreshTrigger={fileGalleryRefresh} />
            </div>
          </div>

          {/* Left Column - Main Info (SECOND on mobile) */}
          <div className="lg:col-span-2 order-2 lg:order-1 space-y-4 sm:space-y-6">
            {/* Project Overview (Client + Property + Scope) */}
            <ProjectOverviewCard project={project} isRestrictedViewer={isRestrictedViewer} />

            {/* Team Card (Sales + Design combined) - View only for restricted viewers or closed projects */}
            <TeamCard
              project={project}
              userRole={user?.role}
              onOpenTeamAssignModal={isTeamLocked ? null : openTeamAssignModal}
              onOpenSalesAgentModal={isTeamLocked ? null : () => setShowSalesAgentModal(true)}
              onOpenAssignSidModal={openAssignSidModal}
              onRemoveTeamMember={isTeamLocked ? null : handleRemoveTeamMember}
              onRemoveSalesAgent={isTeamLocked ? null : handleRemoveSalesAgent}
              onSetPrimaryAgent={isTeamLocked ? null : handleSetPrimaryAgent}
              removingTeamMember={removingTeamMember}
              removingSalesAgent={removingSalesAgent}
              settingPrimaryAgent={settingPrimaryAgent}
              onRefresh={() => fetchProjectById(id)}
              viewOnly={isTeamLocked}
            />

            {/* Pricing Card (Budget + Price History) - hidden for restricted viewers */}
            {!isRestrictedViewer && <PricingCard project={project} />}

            {/* Payment Milestones - only for won projects, hidden for restricted viewers */}
            {!isRestrictedViewer && project.workflowStatus === 'project_closed_won' && (
              <PaymentMilestonesSection
                project={project}
                onSiteEntryCreated={() => fetchProjectById(id)}
              />
            )}

            {/* Timeline Trigger Button - hidden for restricted viewers */}
            {!isRestrictedViewer && (
              <TimelineDrawerTrigger
                eventCount={projectHistory.length}
                onClick={() => setShowTimeline(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile FAB for Quick Actions */}
      {!isRestrictedViewer && (
        <div className="sm:hidden">
          <QuickActionButtons
            project={project}
            userRole={user?.role}
            isMobileFAB={true}
            onActionComplete={(actionType) => {
              fetchProjectById(id);
              fetchProjectHistory();
              if (actionType === 'upload_files') {
                setFileGalleryRefresh(prev => prev + 1);
              }
            }}
          />
        </div>
      )}

      {/* Timeline Drawer */}
      <TimelineDrawer
        isOpen={showTimeline}
        onClose={() => setShowTimeline(false)}
        history={projectHistory}
        project={project}
        loading={loadingHistory}
      />

      {/* Assign SID Modal - Mobile optimized */}
      {showAssignSidModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full sm:mx-4 max-h-[90vh] overflow-auto pb-safe">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {hasProjectSid(project) ? 'Change Assigned SID' : 'Assign SID to Project'}
              </h3>
            </div>
            <div className="px-4 sm:px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Select a Senior Interior Designer (SID) or Assistant SID (ASID) to assign.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select SID/ASID <span className="text-red-500">*</span>
                </label>
                <select
                  className="block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base min-h-[48px] touch-manipulation"
                  value={selectedSidId}
                  onChange={(e) => setSelectedSidId(e.target.value)}
                >
                  <option value="">-- Select SID/ASID --</option>
                  {groupedSids.groups.map(({ sid, asids }) => (
                    <optgroup key={sid.id} label={sid.nickname || sid.fullName}>
                      <option value={sid.id}>
                        {sid.nickname || sid.fullName} (SID)
                      </option>
                      {asids.map((asid) => (
                        <option key={asid.id} value={asid.id}>
                          &nbsp;&nbsp;↳ {asid.nickname || asid.fullName} (ASID)
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  {groupedSids.orphanAsids.length > 0 && (
                    <optgroup label="Unassigned ASID">
                      {groupedSids.orphanAsids.map((asid) => (
                        <option key={asid.id} value={asid.id}>
                          {asid.nickname || asid.fullName} (ASID)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              {hasProjectSid(project) && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Current:</strong> {formatUserName(getProjectSid(project))}
                  </p>
                </div>
              )}
            </div>
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowAssignSidModal(false);
                  setSelectedSidId('');
                }}
                className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[48px] touch-manipulation"
                disabled={assigningSid}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSid}
                className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 min-h-[48px] touch-manipulation transition-all"
                disabled={!selectedSidId || assigningSid}
              >
                {assigningSid ? 'Assigning...' : hasProjectSid(project) ? 'Change SID' : 'Assign SID'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Assignment Modal */}
      <WorkflowAssignmentModal
        isOpen={showTeamAssignModal}
        onClose={() => {
          setShowTeamAssignModal(false);
          setTeamAssignRoleType(null);
        }}
        projectId={id}
        roleType={teamAssignRoleType}
        currentTeamMembers={project?.teamMembers || []}
        transitionWorkflow={false}
        onSuccess={() => fetchProjectById(id)}
      />

      {/* Sales Agent Assignment Modal */}
      <SalesAgentAssignmentModal
        isOpen={showSalesAgentModal}
        onClose={() => setShowSalesAgentModal(false)}
        projectId={id}
        salesLeader={project?.assignment?.assignedLeader}
        onSuccess={() => fetchProjectById(id)}
      />
    </div>
  );
}
